"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Modal, Button, Form, Image } from "react-bootstrap";
import emailjs from "@emailjs/browser";
import "./careers.css";

// --- DYNAMIC UTILITIES ---
const getImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  
  const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
  
  if (url.includes("/uploads/")) {
    const cleanPath = url.substring(url.indexOf("/uploads/"));
    return `${apiUrl}${cleanPath}`;
  }
  
  if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `${apiUrl}${cleanPath}`;
  }
  
  if (url.startsWith("http")) return url;
  return url;
};

const renderCultureIcon = (iconName) => {
  if (!iconName) return <i className="fa-solid fa-star"></i>;
  if (iconName.startsWith("fa-") || iconName.includes("fa-")) {
    const cleanIcon = iconName.includes(" ") ? iconName : `fa-solid ${iconName}`;
    return <i className={cleanIcon}></i>;
  }
  return <span style={{ fontSize: "1.5rem" }}>{iconName}</span>;
};

export default function CareersPage({ initialData }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [careerPageData, setCareerPageData] = useState(initialData || null);

  useEffect(() => {
    if (initialData) {
      return;
    }

    const fetchCareerPage = async () => {
      try {
        const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
        let pageData = null;

        try {
          const res = await fetch(`${apiUrl}/api/career-page/public`, {
            cache: "no-store",
          });
          if (res.ok) {
            const json = await res.json();
            if (json.success && json.data) {
              pageData = json.data;
            }
          }
        } catch (e) {
          console.warn("Failed fetching public career page", e);
        }

        if (pageData) {
          setCareerPageData(pageData);
          setTimeout(() => {
            import("aos").then((mod) => {
              const AOS = mod.default || mod;
              AOS.refreshHard();
            });
          }, 50);
        }
      } catch (err) {
        console.warn("Failed to fetch dynamic career page data. Falling back to static values.", err);
      }
    };
    fetchCareerPage();
  }, [initialData]);


  const accordionData = [
    {
      title: "Software Engineer (AI & ML Integration)",
      items: [
        "Develop and implement AI and machine learning models for custom business solutions.",
        "Work closely with our data engineering team to build scalable data pipelines.",
        "Experience with Python, TensorFlow, and cloud services like AWS or Azure preferred.",
      ],
    },
    {
      title: "Cloud Infrastructure Specialist",
      items: [
        "Optimize and manage cloud environments, ensuring performance, cost-efficiency, and security.",
        "Expertise in multi-cloud strategies, Kubernetes, and DevOps practices is a plus.",
        "Hands-on experience with cloud providers such as AWS, Google Cloud, or Azure.",
      ],
    },
    {
      title: "Full-Stack Developer (SaaS Focus)",
      items: [
        "Design and develop scalable SaaS solutions using modern frameworks like Ruby on Rails and React.",
        "Collaborate closely with our UI/UX designers to ensure seamless user experiences.",
        "Prior experience in cloud-native application development is highly desired.",
      ],
    },
    {
      title: "UI/UX Designer",
      items: [
        "Create intuitive user interfaces and experiences for our clients’ web and mobile applications.",
        "Strong background in responsive design, user research, and wireframing tools like Figma or Sketch.",
        "A portfolio showcasing previous UI/UX work is required.",
      ],
    },
  ];

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const [show, setShow] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedCandidate, setSubmittedCandidate] = useState({ name: "", position: "" });
  const [formAlert, setFormAlert] = useState(null); // { message: string, type: 'error' | 'warning' }
  const [errorField, setErrorField] = useState(null); // 'name' | 'email' | 'phone' | 'position' | 'message' | 'resume'

  const handleClose = () => {
    setShow(false);
    setFormAlert(null);
    setErrorField(null);
  };
  const handleShow = () => {
    setShow(true);
    setFormAlert(null);
    setErrorField(null);
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    linkedinUrl: "",
    message: "",
  });
  const [resumeFile, setResumeFile] = useState(null);
  const fileInputRef = useRef(null);
  const modalBodyRef = useRef(null);

  // Always reset scroll to top whenever the modal is opened
  useEffect(() => {
    if (show) {
      setTimeout(() => {
        if (modalBodyRef.current) {
          modalBodyRef.current.scrollTop = 0;
        }
      }, 30);
    }
  }, [show]);

  const showAlert = (message, type = "error", field = null) => {
    setFormAlert({ message, type });
    if (field) {
      setErrorField(field);
      setTimeout(() => {
        if (field === "resume") {
          const zone = document.querySelector(".cq-file-zone");
          if (zone) zone.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } else {
          const el = document.getElementById(field);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }, 50);
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errorField === id) {
      setErrorField(null);
      setFormAlert(null);
    }
  };

  const handleResumeSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setResumeFile(file);
      if (errorField === "resume") {
        setErrorField(null);
        setFormAlert(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validate Full Name
    if (!formData.name?.trim()) {
      showAlert("Please enter your Full Name.", "error", "name");
      return;
    }

    // 2. Validate Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email?.trim() || !emailRegex.test(formData.email.trim())) {
      showAlert("Please enter a valid Email Address.", "error", "email");
      return;
    }

    // 3. Validate Phone (10 digits)
    const phoneDigits = formData.phone?.replace(/[^0-9]/g, "");
    if (!phoneDigits || phoneDigits.length !== 10) {
      showAlert("Please enter a valid 10-digit Phone Number.", "error", "phone");
      return;
    }

    // 4. Validate Position
    if (!formData.position) {
      showAlert("Please select a Position to apply for.", "error", "position");
      return;
    }

    // 5. Validate Cover Message
    if (!formData.message?.trim()) {
      showAlert("Please write a short Cover Message.", "error", "message");
      return;
    }

    // 6. Validate Resume Upload
    if (!resumeFile) {
      showAlert("Please upload your Resume (PDF, DOC, DOCX).", "error", "resume");
      return;
    }

    // 7. Validate Resume File Extension
    const allowedExtensions = /(.pdf|.doc|.docx)$/i;
    if (!allowedExtensions.exec(resumeFile.name)) {
      showAlert("Invalid file type. Please upload a PDF, DOC, or DOCX resume.", "error", "resume");
      return;
    }

    setFormAlert(null);
    setErrorField(null);
    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name.trim());
    formDataToSend.append("email", formData.email.trim());
    formDataToSend.append("phone", phoneDigits);
    formDataToSend.append("position", formData.position);
    formDataToSend.append("linkedinUrl", formData.linkedinUrl?.trim() || "");
    formDataToSend.append("message", formData.message.trim());
    formDataToSend.append("resume", resumeFile);

    try {
      const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
      const res = await fetch(`${apiUrl}/api/careers/submit`, {
        method: "POST",
        body: formDataToSend,
      });

      let data = {};
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await res.json();
      }

      if (res.ok) {
        setSubmittedCandidate({
          name: formData.name,
          position: formData.position
        });

        // 1. First close the form popup immediately
        handleClose();

        // 2. Reset form data
        setFormData({
          name: "",
          email: "",
          phone: "",
          position: "",
          linkedinUrl: "",
          message: "",
        });
        setResumeFile(null);

        // 3. Show beautiful custom theme success popup
        setTimeout(() => {
          setShowSuccessModal(true);
        }, 200);
      } else {
        showAlert(data.message || "Failed to submit application. Please try again.", "error");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      showAlert("Network error. Please check your connection and try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // AOS is globally initialized by AOSProvider — no need to re-init here

  return (
    <>
      <div suppressHydrationWarning>
        {/* Banner Section */}
        <section suppressHydrationWarning>
          <div className="container" data-aos="fade-down" data-aos-duration="800" suppressHydrationWarning>
            <div className="team-sectionA row align-items-center mt-5" suppressHydrationWarning>
              <div className="team-textA col-md-6">
                <h1 className="fw-bold text-white fs-1">
                  {careerPageData?.bannerSection?.title || "Join the CodeQlik Team"}
                </h1>
                <p>
                  {careerPageData?.bannerSection?.description || `Are you ready to work on cutting-edge projects and build
                  world-class software solutions? At CodeQlik, we're
                  constantly seeking talented professionals who share our
                  passion for innovation and excellence in technology. Be part
                  of a dynamic team that's driving digital transformation for
                  businesses across the U.S.!`}
                </p>
              </div>
              <div className="team-illustrationA col-md-6 text-center" suppressHydrationWarning>
                <Image
                  src={getImageUrl(careerPageData?.bannerSection?.image?.url) || "/assets/img/career home.svg"}
                  alt={careerPageData?.bannerSection?.image?.alt || "Illustration of a team working"}
                  fetchPriority="high"
                  fluid
                  suppressHydrationWarning
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Work Section */}
        <section>
          <div className="container py-5">
            <h2 className="career-heading text-center fw-bold mb-4">
              {careerPageData?.whyWorkSection?.title || "Why Work at CodeQlik?"}
            </h2>
            <div className="row g-4">
              {careerPageData?.whyWorkSection?.cards?.length > 0 ? (
                careerPageData.whyWorkSection.cards.map((card, idx) => (
                  <div
                    className="col-md-6 col-sm-12 d-flex"
                    key={idx}
                    data-aos="fade-up"
                    
                  >
                    <div className="feature-box1 p-4 w-100">
                      <div className="feature-number1">{(idx + 1).toString().padStart(2, '0')}</div>
                      <div className="feature-title1">{card.title}</div>
                      <p className="work-text-career">{card.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <div className="col-md-6 col-sm-12 d-flex" data-aos="fade-up" >
                    <div className="feature-box1 p-4 w-100">
                      <div className="feature-number1">01</div>
                      <div className="feature-title1">Innovative Projects</div>
                      <p className="work-text-career">
                        Get hands-on with AI, Machine Learning, Cloud Solutions, and
                        SaaS development. Shape the future of tech with real impact.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6 col-sm-12 d-flex" data-aos="fade-up" >
                    <div className="feature-box1 p-4 w-100">
                      <div className="feature-number1">02</div>
                      <div className="feature-title1">Collaborative Environment</div>
                      <p className="work-text-career">
                        Work alongside industry experts who foster creativity, share
                        knowledge, and celebrate achievements.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6 col-sm-12 d-flex" data-aos="fade-up" >
                    <div className="feature-box1 p-4 w-100">
                      <div className="feature-number1">03</div>
                      <div className="feature-title1">Growth and Development</div>
                      <p className="work-text-career">
                        We invest in our people with opportunities for career
                        advancement, skill enhancement, and professional growth.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-6 col-sm-12 d-flex" data-aos="fade-up" >
                    <div className="feature-box1 p-4 w-100">
                      <div className="feature-number1">04</div>
                      <div className="feature-title1">Flexible Work Culture</div>
                      <p className="work-text-career">
                        Balance life and work with remote options and flexible
                        schedules to perform at your best.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Current Openings Section */}
        <section>
          <div className="container mt-5">
            <h2 className="fw-bold mb-4 career-heading">
              {careerPageData?.openingSection?.title || "Current Openings"}
            </h2>
            {(() => {
              const openings = careerPageData?.openingSection?.positions || [];
              if (openings.length > 0) {
                return openings.map((item, index) => {
                  const rawLines = item.shortDescription
                    ? item.shortDescription.split('\n').map(l => l.replace(/\r/g, '').trimEnd()).filter(l => l.trim() !== '')
                    : [];
                  return (
                    <div
                      className="accordion-career"
                      key={index}
                      data-aos="fade-up"
                      
                    >
                      <div
                        className="accordion-header d-flex justify-content-between align-items-center"
                        onClick={() => toggleAccordion(index)}
                        style={{ cursor: "pointer" }}
                      >
                        <h3>{item.title}</h3>
                        <div className="caret-box-career">
                          <span className="caret">
                            <i className={`fa-solid ${openIndex === index ? "fa-minus" : "fa-plus"}`}></i>
                          </span>
                        </div>
                      </div>
                      {openIndex === index && (
                        <div className={`accordion-content ${openIndex === index ? "show" : ""}`}>
                          {rawLines.length > 0 ? (
                            <div className="career-text">
                              {(() => {
                                let currentSectionIsList = false;
                                return rawLines.map((line, i) => {
                                  const trimmed = line.trim();
                                  if (!trimmed) return null;

                                  // Heading detection: ends with ':' or common section title words
                                  const isHeading = trimmed.endsWith(':') || /^(responsibilities|requirements|qualifications|skills|what you'll do|what we offer|perks|benefits|key requirements|about the role|eligibility)$/i.test(trimmed);

                                  if (isHeading) {
                                    currentSectionIsList = true;
                                    const headingText = trimmed.endsWith(':') ? trimmed : `${trimmed}:`;
                                    return <p key={i} className="career-section-heading">{headingText}</p>;
                                  }

                                  // Explicit bullet symbols detection (-, *, •, ▸, –, —, +, >, 1., etc.)
                                  const hasExplicitBullet = /^[\s\*\-•▸–—+>]/.test(line) || /^\d+[\.\)]\s/.test(trimmed);
                                  const cleanText = trimmed.replace(/^[\s\*\-•▸–—+>]+/, '').replace(/^\d+[\.\)]\s*/, '').trim();

                                  // If line has an explicit bullet OR is located under a list section (e.g. RESPONSIBILITIES:)
                                  if (hasExplicitBullet || currentSectionIsList) {
                                    return (
                                      <div key={i} className="career-bullet-line">
                                        <span className="career-bullet-dot">▸</span>
                                        <span>{cleanText}</span>
                                      </div>
                                    );
                                  }

                                  return <p key={i} className="career-text-line">{cleanText}</p>;
                                });
                              })()}
                            </div>
                          ) : (
                            <p className="career-text px-4 py-2">No description details available.</p>
                          )}
                          <div className="text-center py-2">
                            <button
                              className="btn apply-job"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, position: item.title }));
                                handleShow();
                              }}
                            >
                              Apply Now
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
              } else {
                return accordionData.map((item, index) => (
                  <div
                    className="accordion-career"
                    key={index}
                    data-aos="fade-up"
                    
                  >
                    <div
                      className="accordion-header d-flex justify-content-between align-items-center"
                      onClick={() => toggleAccordion(index)}
                      style={{ cursor: "pointer" }}
                    >
                      <h3>{item.title}</h3>
                      <div className="caret-box-career">
                        <span className="caret">
                          <i
                            className={`fa-solid ${
                              openIndex === index ? "fa-minus" : "fa-plus"
                            }`}
                          ></i>
                        </span>
                      </div>
                    </div>
                    {openIndex === index && (
                      <div
                        className={`accordion-content ${
                          openIndex === index ? "show" : ""
                        }`}
                      >
                        <ul className="career-text">
                          {item.items.map((text, i) => (
                            <li key={i}>{text}</li>
                          ))}
                        </ul>
                        <div className="text-center py-2">
                          <button
                            className="btn apply-job"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, position: item.title }));
                              handleShow();
                            }}
                          >
                            Apply Now
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ));
              }
            })()}
          </div>
          <div className="text-center mt-4">
            <button className="btn apply-job" onClick={handleShow}>
              Apply for a Different Role
            </button>
          </div>

          {/* Premium Apply Modal */}
          {show && (
            <>
              <div className="modal-backdrop fade show" onClick={handleClose} />
              <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                  <div className="modal-content cq-modal-card">

                    {/* Header */}
                    <div className="cq-modal-header">
                      <div className="cq-modal-icon-wrap">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </div>
                      <div className="cq-modal-title-wrap">
                        <h5 className="cq-modal-title">Apply for a Job</h5>
                        <p className="cq-modal-subtitle">Join CodeQlik — fill in your details below</p>
                      </div>
                      <button type="button" className="cq-close-btn" aria-label="Close" onClick={handleClose}>
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </div>

                    {/* Notification Alert Bar */}
                    {formAlert && (
                      <div
                        style={{
                          margin: '12px 18px 0',
                          padding: '10px 14px',
                          borderRadius: '10px',
                          background: formAlert.type === 'error'
                            ? 'linear-gradient(90deg, rgba(239, 68, 68, 0.16) 0%, rgba(26, 16, 16, 0.95) 100%)'
                            : 'linear-gradient(90deg, rgba(241, 131, 59, 0.16) 0%, rgba(26, 20, 16, 0.95) 100%)',
                          border: formAlert.type === 'error'
                            ? '1px solid rgba(239, 68, 68, 0.45)'
                            : '1px solid rgba(241, 131, 59, 0.45)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          animation: 'cqFadeInDown 0.25s ease-out',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)'
                        }}
                      >
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: formAlert.type === 'error' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(241, 131, 59, 0.2)',
                            color: formAlert.type === 'error' ? '#ef4444' : '#f1833b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}
                        >
                          <i className="fa-solid fa-triangle-exclamation"></i>
                        </div>
                        <span
                          style={{
                            color: '#ffffff',
                            fontSize: '0.84rem',
                            fontWeight: 500,
                            flex: 1,
                            lineHeight: 1.4
                          }}
                        >
                          {formAlert.message}
                        </span>
                        <button
                          type="button"
                          onClick={() => setFormAlert(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'rgba(255, 255, 255, 0.5)',
                            cursor: 'pointer',
                            fontSize: '18px',
                            lineHeight: 1,
                            padding: '0 4px'
                          }}
                          aria-label="Dismiss alert"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    <Form onSubmit={handleSubmit} noValidate>
                      <div className="cq-modal-body" ref={modalBodyRef}>
                        <div className="row g-4">

                          {/* Name */}
                          <div className="col-md-6">
                            <div className="cq-field">
                              <label className="cq-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                Full Name
                              </label>
                              <input 
                                id="name" 
                                className={`cq-input ${errorField === "name" ? "cq-input--error" : ""}`} 
                                placeholder="e.g. John Doe" 
                                value={formData.name} 
                                onChange={handleChange} 
                                required 
                              />
                            </div>
                          </div>

                          {/* Email */}
                          <div className="col-md-6">
                            <div className="cq-field">
                              <label className="cq-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                Email Address
                              </label>
                              <input 
                                type="email" 
                                id="email" 
                                className={`cq-input ${errorField === "email" ? "cq-input--error" : ""}`} 
                                placeholder="name@example.com" 
                                value={formData.email} 
                                onChange={handleChange} 
                                required 
                              />
                            </div>
                          </div>

                          {/* Phone */}
                          <div className="col-md-6">
                            <div className="cq-field">
                              <label className="cq-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.08 3.4 2 2 0 0 1 3.07 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                Phone Number
                              </label>
                              <input 
                                type="tel" 
                                id="phone" 
                                className={`cq-input ${errorField === "phone" ? "cq-input--error" : ""}`} 
                                placeholder="0123456789" 
                                value={formData.phone} 
                                onChange={handleChange} 
                                maxLength="10" 
                                pattern="[0-9]{10}" 
                                required 
                              />
                            </div>
                          </div>

                          {/* Position */}
                          <div className="col-md-6">
                            <div className="cq-field">
                              <label className="cq-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                                Position Applied
                              </label>
                              <select 
                                id="position" 
                                className={`cq-input cq-select ${formData.position === "" ? "cq-select-placeholder" : ""} ${errorField === "position" ? "cq-input--error" : ""}`} 
                                value={formData.position} 
                                onChange={handleChange} 
                                required
                              >
                                <option value="" disabled>Select a Position</option>
                                {careerPageData?.openingSection?.positions?.length > 0 ? (
                                  careerPageData.openingSection.positions.map((pos, idx) => (
                                    <option key={idx} value={pos.title}>{pos.title}</option>
                                  ))
                                ) : (
                                  <>
                                    <option>Software Engineer</option>
                                    <option>Web Developer</option>
                                    <option>UI/UX Designer</option>
                                    <option>Digital Marketer</option>
                                    <option>Project Manager</option>
                                    <option>Internship</option>
                                  </>
                                )}
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>

                          {/* LinkedIn URL */}
                          <div className="col-12">
                            <div className="cq-field">
                              <label className="cq-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                                  <rect x="2" y="9" width="4" height="12"/>
                                  <circle cx="4" cy="4" r="2"/>
                                </svg>
                                LinkedIn URL
                              </label>
                              <input 
                                type="url" 
                                id="linkedinUrl" 
                                className="cq-input" 
                                placeholder="https://linkedin.com/in/username" 
                                value={formData.linkedinUrl} 
                                onChange={handleChange} 
                              />
                            </div>
                          </div>

                          {/* Message */}
                          <div className="col-12">
                            <div className="cq-field">
                              <label className="cq-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                Cover Message
                              </label>
                              <textarea 
                                id="message" 
                                className={`cq-input cq-textarea ${errorField === "message" ? "cq-input--error" : ""}`} 
                                placeholder="Tell us about yourself and why you'd be a great fit..." 
                                value={formData.message} 
                                onChange={handleChange} 
                                required 
                              />
                            </div>
                          </div>

                          {/* Resume Upload */}
                          <div className="col-12">
                            <div className="cq-field">
                              <label className="cq-label">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                Upload Resume
                                <span className="cq-label-hint">PDF, DOC, DOCX</span>
                              </label>
                              <input 
                                ref={fileInputRef} 
                                type="file" 
                                accept=".pdf,.doc,.docx" 
                                onChange={handleResumeSelect} 
                                style={{ display: 'none' }} 
                              />
                              <div 
                                className={`cq-file-zone ${resumeFile ? 'cq-file-zone--done' : ''} ${errorField === 'resume' ? 'cq-file-zone--error' : ''}`} 
                                onClick={() => fileInputRef.current?.click()}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={resumeFile ? '#f1833b' : (errorField === 'resume' ? '#ef4444' : '#888')} strokeWidth="2" strokeLinecap="round">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                                </svg>
                                <span className="cq-file-text" style={{ color: errorField === 'resume' && !resumeFile ? '#fca5a5' : undefined }}>
                                  {resumeFile ? resumeFile.name : (errorField === 'resume' ? 'Please click here to upload your resume' : 'Click to browse your resume')}
                                </span>
                                <span className={`cq-file-badge ${resumeFile ? 'cq-file-badge--done' : ''}`} style={errorField === 'resume' && !resumeFile ? { background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.5)' } : undefined}>
                                  {resumeFile ? '✓ Ready' : (errorField === 'resume' ? 'Required' : 'Choose File')}
                                </span>
                              </div>
                              {errorField === 'resume' && !resumeFile && (
                                <div style={{ color: '#ef4444', fontSize: '0.76rem', marginTop: '5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <i className="fa-solid fa-circle-exclamation"></i>
                                  <span>Resume is required to submit this application.</span>
                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>

                      {/* Footer */}
                      <div className="cq-modal-footer">
                        <button type="button" className="cq-btn-secondary" onClick={handleClose} disabled={isSubmitting}>Cancel</button>
                        <button type="submit" className="cq-btn-primary" disabled={isSubmitting}>
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                              Submit Application
                            </>
                          )}
                        </button>
                      </div>
                    </Form>

                  </div>
                </div>
              </div>
            </>
          )}

          {/* Theme-matching Custom Success Modal (Self-contained, immune to Bootstrap class collision) */}
          {showSuccessModal && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 999999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
              }}
              onClick={() => setShowSuccessModal(false)}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: '460px',
                  background: 'linear-gradient(150deg, #181818 0%, #0e0e0e 100%)',
                  border: '1px solid rgba(241, 131, 59, 0.35)',
                  borderRadius: '24px',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.95), 0 0 40px rgba(241, 131, 59, 0.15)',
                  padding: '36px 28px 30px',
                  textAlign: 'center',
                  color: '#ffffff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Glowing Checkmark Circle */}
                <div
                  style={{
                    position: 'relative',
                    width: '82px',
                    height: '82px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(241, 131, 59, 0.25) 0%, rgba(241, 131, 59, 0.05) 70%, transparent 100%)',
                    border: '2px solid rgba(241, 131, 59, 0.55)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 30px rgba(241, 131, 59, 0.25)',
                    marginTop: '6px'
                  }}
                >
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#f1833b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>

                {/* Title */}
                <div>
                  <h3
                    style={{
                      color: '#ffffff',
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      margin: 0,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2
                    }}
                  >
                    Application <span style={{ color: '#f1833b' }}>Submitted!</span>
                  </h3>
                </div>
                
                {/* Description */}
                <p
                  style={{
                    color: 'rgba(255, 255, 255, 0.75)',
                    fontSize: '0.92rem',
                    lineHeight: 1.6,
                    margin: 0,
                    maxWidth: '380px'
                  }}
                >
                  Thank you{submittedCandidate.name ? `, ${submittedCandidate.name}` : ''}! Your application for <strong style={{ color: '#ffffff', fontWeight: 700 }}>{submittedCandidate.position || 'this position'}</strong> has been received. Our team will review your profile and get in touch with you soon.
                </p>

                {/* Primary Button */}
                <button 
                  type="button" 
                  onClick={() => setShowSuccessModal(false)}
                  style={{
                    width: '100%',
                    maxWidth: '260px',
                    background: 'linear-gradient(135deg, #f1833b 0%, #e0722a 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.92rem',
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 18px rgba(241, 131, 59, 0.35)',
                    transition: 'all 0.2s',
                    marginTop: '4px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(241, 131, 59, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 18px rgba(241, 131, 59, 0.35)';
                  }}
                >
                  Got it, Thanks!
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Expect Section */}
        <section>
          <div className="container custom-containerA">
            <div className="row align-items-center">
              <div className="col-md-7">
                <h2 className="section-titleA">{careerPageData?.expectSection?.title || "What You Can Expect"}</h2>
                {careerPageData?.expectSection?.description && (
                  <p className="text-career1 mb-4">{careerPageData.expectSection.description}</p>
                )}
                {careerPageData?.expectSection?.cards?.length > 0 ? (
                  careerPageData.expectSection.cards.map((card, idx) => (
                    <div className="mt-4" data-aos="fade-up"  key={idx}>
                      <h5 className="highlightA">{card.title}</h5>
                      <p className="text-career1">{card.description}</p>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="mt-4" data-aos="fade-up" >
                      <h5 className="highlightA">Competitive Salary & Benefits</h5>
                      <p className="text-career1">
                        We offer a market-competitive salary, healthcare, and
                        benefits package, including wellness programs.
                      </p>
                    </div>
                    <div className="mt-4" data-aos="fade-up" >
                      <h5 className="highlightA">Remote-Friendly Work Options</h5>
                      <p className="text-career1">
                        {`Whether you're at home, in one of our global offices, or
                        traveling, you have the flexibility to work where you're
                        most comfortable.`}
                      </p>
                    </div>
                    <div className="mt-4" data-aos="fade-up" >
                      <h5 className="highlightA">A Chance to Innovate</h5>
                      <p className="text-career1">
                        {`Be part of a team that's not just adopting new technologies
                        but creating them. From AI-native development to cloud
                        infrastructure optimization, we’re shaping the future.`}
                      </p>
                    </div>
                  </>
                )}
              </div>
              <div
                className="col-md-5 text-center img-containerA"
                data-aos="fade-up"
                
              >
                <Image
                  src={getImageUrl(careerPageData?.expectSection?.image?.url) || "/assets/img/image_processing2021.svg"}
                  alt={careerPageData?.expectSection?.image?.alt || "Illustration showing innovation"}
                  fluid
                />
              </div>
            </div>
          </div>
        </section>

        {/* Culture & Values Section */}
        <section>
          <div className="container values-section">
            <h2 className="values-title">{careerPageData?.cultureSection?.title || "Our Culture and Values"}</h2>
            <p className="values-text">
              {careerPageData?.cultureSection?.description || `At CodeQlik, we're more than just a tech company. We're a
              community of innovators, problem-solvers, and collaborators who
              value:`}
            </p>
            <div className="row g-4 justify-content-center">
              {careerPageData?.cultureSection?.cards?.length > 0 ? (
                careerPageData.cultureSection.cards.map((card, idx) => (
                  <div
                    className="col-lg-4 col-sm-4 d-flex"
                    data-aos="fade-up"
                    
                    key={idx}
                  >
                    <div className="value-card w-100 text-center">
                      <div className="value-icon">
                        {renderCultureIcon(card.icon)}
                      </div>
                      <h5 className="value-title">{card.title}</h5>
                      <p className="value-description">{card.description}</p>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <div
                    className="col-lg-4 col-sm-4"
                    data-aos="fade-up"
                    
                  >
                    <div className="value-card ">
                      <div className="value-icon">
                        <i className="fa-solid fa-bolt"></i>
                      </div>
                      <h5 className="value-title">Excellence</h5>
                      <p className="value-description">
                        Delivering the highest quality in every project.
                      </p>
                    </div>
                  </div>
                  <div
                    className="col-lg-4 col-sm-4"
                    data-aos="fade-up"
                    
                  >
                    <div className="value-card text-center">
                      <div className="value-icon">🔄</div>
                      <h5 className="value-title">Curiosity</h5>
                      <p className="value-description">
                        Exploring new technologies and challenging the status quo.
                      </p>
                    </div>
                  </div>
                  <div
                    className="col-lg-4 col-sm-4"
                    data-aos="fade-up"
                    
                  >
                    <div className="value-card text-center">
                      <div className="value-icon">
                        <i className="fa-solid fa-crown"></i>
                      </div>
                      <h5 className="value-title">Empathy</h5>
                      <p className="value-description">
                        Understanding our clients, users, and teammates to build
                        better solutions.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* How to Apply Section */}
        <section>
          <div className="container">
            <div
              className="apply-section"
              data-aos="fade-up"
              
            >
              <h2 className="fw-bold">How to Apply</h2>
              <p>{`Interested in joining our team? We'd love to hear from you!`}</p>
              <p>
                Send your resume and portfolio to{" "}
                <strong>careers@CodeQlik.dev</strong>
              </p>
              <button className="btn apply-job" onClick={handleShow}>
                Apply Now
              </button>
            </div>
          </div>
        </section>

        {/* Stay Connected Section */}
        <section>
          <div className="container stay-connected">
            <div
              className="row align-items-center"
              data-aos="fade-up"
              
            >
              <div className="col-lg-6 text-center text-lg-start">
                <h2 className="fw-bold career-heading">{careerPageData?.stayConnectedSection?.title || "Stay Connected"}</h2>
                <p>
                  {careerPageData?.stayConnectedSection?.description || `Follow us on LinkedIn and Facebook to keep up with the latest
                  job opportunities, company news, and tech insights from
                  CodeQlik.`}
                </p>
              </div>
              <div className="col-lg-6 text-center illustrationB">
                <Image
                  src={getImageUrl(careerPageData?.stayConnectedSection?.image?.url) || "/assets/img/career-last.jpg"}
                  alt={careerPageData?.stayConnectedSection?.image?.alt || "Rocket Launch Illustration"}
                  fluid
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
